<template>
  <div
    class="product-card"
    :data-product-brand="brandName"
    :data-product-id="productId"
    :data-product-name="productName"
  >
    <div class="product-card-image">
      <div class="product-card-header">
        <div class="ad-container">
          <UiAd v-if="adProps" v-bind="adProps" />
        </div>

        <component
          :is="isWishlisted ? UiSmartLink : 'div'"
          :to="isWishlisted ? USER_WISHLISTED_PAGE_PATH : null"
          class="wishlist-container"
        >
          <UiIcon v-bind="wishlistIconProps" @clicked="emitAddToWishlist" />
        </component>
      </div>

      <UiSmartLink
        :class="['product-card-link', { oos: soldOut }]"
        :to="pdpUrl"
      >
        <span v-if="badgeText" class="product-badge headline-s">{{
          badgeText
        }}</span>

        <UiSmartImage v-bind="image" aspect-ratio="1/1" :alt="productName" />

        <div v-if="soldOut" class="oos-label">
          {{ labels.outOfStock }}
        </div>
      </UiSmartLink>

      <UiButton
        v-if="props.showRemindMe"
        class="remind-me-button"
        :is-disabled="props.isRemindMeDisabled || isNotificationSubscribed"
        :text="labels.remindMe"
        :type="BtnType.SECONDARY"
        @clicked="emitRemindMe"
      />
      <UiButton
        v-else-if="soldOut"
        class="waitlist-button"
        :is-disabled="isWaitlisted || isWaitlisting"
        :text="waitlistCtaLabel"
        :type="BtnType.SECONDARY"
        @clicked="emitAddToWaitlist"
      />
      <UiButton
        v-else-if="!props.disableAtbButton"
        class="add-to-bag-button"
        :is-disabled="isAddingToBag"
        :text="isAddingToBag ? labels.addingToBag : labels.addToBag"
        :type="BtnType.PRIMARY"
        @clicked="emitAddToBag"
      />
    </div>

    <div class="product-card-description body-m">
      <UiSmartLink class="product-card-link" :to="pdpUrl">
        <p v-if="tagsLabel" class="product-tags caption-s">
          {{ tagsLabel }}
        </p>
        <p class="brand-name headline-m">{{ brandName }}</p>
        <p class="product-name">{{ productName }}</p>
        <UiPrice
          :original-price="originalPrice"
          :sales-price="onSale ? price : ''"
          :sales-text="saleText || ''"
        />
        <UiRating
          v-if="rating"
          type="default"
          :rating-count="rating"
          :review-text="reviewsCountLabel"
        />
        <span v-if="variantsCount > 1" class="variant-count">
          {{ variantsCountLabel }}
        </span>
      </UiSmartLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import UiAd from '@sephora-asia/ui-ad'
import UiButton from '@sephora-asia/ui-button/UiButton.vue'
import UiIcon from '@sephora-asia/ui-icon'
import UiPrice from '@sephora-asia/ui-price/UiPrice.vue'
import UiRating from '@sephora-asia/ui-rating/UiRating.vue'
import UiSmartImage from '@sephora-asia/ui-smart-image'
import UiSmartLink from '@sephora-asia/ui-smart-link/UiSmartLink.vue'
import { pluralize } from 'inflection'

import { IconLabel, type Props as AdProps } from '@sephora-asia/ui-ad/types'
import { BtnType } from '@sephora-asia/ui-button/types'
import type { IconProps } from '@sephora-asia/ui-icon/types'
import type { ProductProps, ProductLabel } from './types'
import { DefaultLabels } from './enums'

// ----- Props -----

const props = withDefaults(defineProps<ProductProps>(), {
  isAddingToBag: false,
  isLoggedIn: false,
  isWaitlisting: false,
  originalPrice: '',
  tags: () => [],
  badgeText: '',
  rating: 0,
  reviewsCount: 0,
  variantsCount: 1,
  disableAtbButton: false,
  showRemindMe: false,
  isRemindMeDisabled: false,
  saleText: '',
  waitlistedVariantIds: () => [],
  wishlistedVariantIds: () => [],
  notificationSubscribedVariantIds: () => [],
})

const USER_WISHLISTED_PAGE_PATH = '/wishlist'

// ----- Events -----

interface Emits {
  (e: 'add-to-waitlist', variantId: string): void
  (e: 'add-to-wishlist', variantId: string): void
  (e: 'add-to-bag', variantId: string): void
  (e: 'remind-me', variantId: string): void
}

const emit = defineEmits<Emits>()

const emitAddToWishlist = function (): void {
  !isWishlisted.value &&
    emit('add-to-wishlist', props.featuredVariantId.toString())
}

const emitAddToWaitlist = function (): void {
  emit('add-to-waitlist', props.featuredVariantId.toString())
}

const emitRemindMe = function (): void {
  !isNotificationSubscribed.value &&
    emit('remind-me', props.featuredVariantId.toString())
}

const emitAddToBag = function (): void {
  emit('add-to-bag', props.featuredVariantId.toString())
}

// ----- Computed Properties -----

const isWishlisted = computed(
  (): boolean =>
    props.isLoggedIn &&
    props.wishlistedVariantIds?.includes(props.featuredVariantId.toString())
)

const isWaitlisted = computed(
  (): boolean =>
    props.isLoggedIn &&
    props.waitlistedVariantIds?.includes(props.featuredVariantId.toString())
)

const isNotificationSubscribed = computed(
  (): boolean =>
    props.isLoggedIn &&
    props.notificationSubscribedVariantIds?.includes(
      props.featuredVariantId.toString()
    )
)

const wishlistIconProps = computed(
  (): IconProps => ({
    colorFill: isWishlisted.value
      ? 'var(--color-base-red-100)'
      : 'var(--color-base-black)',
    colorHover: 'var(--color-base-red-100)',
    name: `heart-${isWishlisted.value ? 'filled' : 'outlined'}`,
    size: '32px',
    isClickable: true,
  })
)

// DefineProps cannot specify default values for missing keys inside an object if in case some keys are missing.
const labels = computed(
  (): Required<ProductLabel> => ({
    addingToBag: DefaultLabels.ADDING_TO_BAG,
    addToBag: DefaultLabels.ADD_TO_BAG,
    outOfStock: DefaultLabels.OUT_OF_STOCK,
    remindMe: DefaultLabels.REMIND_ME,
    waitlisted: DefaultLabels.WAITLISTED,
    waitlisting: DefaultLabels.WAITLISTING,
    waitlistMe: DefaultLabels.WAITLIST_ME,
    selectedTypesOnly: `Selected ${props.optionType}s Only`,
    ...props.label,
  })
)

const waitlistCtaLabel = computed((): string =>
  isWaitlisted.value
    ? labels.value.waitlisted
    : props.isWaitlisting
    ? labels.value.waitlisting
    : labels.value.waitlistMe
)

const adProps = computed((): AdProps | undefined => {
  if (props.ad?.id && props.ad?.title) {
    return {
      title: props.ad.title,
      subtitle: labels.value.selectedTypesOnly,
      iconName:
        IconLabel[
          props.ad?.iconType?.toUpperCase?.() as keyof typeof IconLabel
        ] || IconLabel.PROMOTION,
    }
  }
  return undefined
})

const variantsCountLabel = computed((): string =>
  props.variantsCount > 1
    ? `${props.variantsCount} ${pluralize(props.optionType)}`
    : ''
)

const reviewsCountLabel = computed((): string =>
  props.reviewsCount ? `(${props.reviewsCount})` : ''
)

const tagsLabel = computed((): string => props.tags?.join(' | '))
</script>

<style lang="scss" scoped>
@mixin text-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;

  .product-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 40px;

    .ad-container {
      display: flex;
      flex-direction: column;
      overflow: hidden;

      @include mobile {
        padding-right: 28px;
      }
    }

    :deep(.ad-container-title),
    :deep(.ad-container-subtitle) {
      display: block;
      @include text-ellipsis;
    }

    .wishlist-container {
      display: flex;
      visibility: hidden;
      overflow: visible;
      background-color: rgba(var(--color-rgb-base-white), 0.6);
      z-index: 1;
      padding: 4px;
      justify-content: center;
      align-items: center;
      border-radius: 50%;

      @include mobile {
        visibility: visible;
        position: absolute;
        right: 0;
        bottom: 0;
      }
    }
  }

  .product-card-image {
    position: relative;

    .product-card-link {
      display: block;
      position: relative;

      .product-badge {
        position: absolute;
        top: 4px;
        max-width: 100%;
        align-content: center;
        height: 20px;
        padding-inline: 8px;
        border-radius: $size-radius-low;
        color: $color-font-white;
        background-color: $color-base-red-70;
        z-index: 1;
        @include text-ellipsis;
      }

      &.oos {
        .product-badge {
          opacity: 0.75;
        }
        .smart-image {
          opacity: 0.6;
        }
      }
    }

    .oos-label {
      @extend .headline-m;

      background-color: var(--color-base-white);
      color: var(--color-base-grey-50);
      left: 50%;
      opacity: 0.75;
      padding: 9.5px 0;
      position: absolute;
      text-align: center;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 100%;
    }

    .add-to-bag-button,
    .remind-me-button,
    .waitlist-button {
      visibility: hidden;
      position: absolute;
      bottom: 0;
      width: 100%;
      text-align: center;

      :deep(.btn-content .text) {
        width: 100%;
      }

      @include mobile {
        display: none;
      }
    }
  }

  &:hover {
    :is(
        .add-to-bag-button,
        .waitlist-button,
        .remind-me-button,
        .wishlist-container
      ) {
      visibility: visible;
    }
  }

  .product-card-link:link,
  .product-card-link:visited {
    text-decoration: none;
    color: var(--color-base-black);
  }

  .product-card-description {
    .product-card-link {
      display: flex;
      flex-direction: column;
      gap: 2px;

      .product-tags {
        color: var(--color-base-red-100);
        @include text-ellipsis;
      }

      .brand-name {
        @include text-ellipsis;
      }

      .product-name {
        @include lineclamp(2);
      }

      :deep(.price-container) {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
      }

      .variant-count {
        color: var(--color-base-grey-50);
      }
    }
  }
}
</style>
